import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import { SALES_CALENDAR_URL, SALES_CONTACT_EMAIL } from '../../config/featureFlags';
import { BILLING_PACKAGES, CONTACT_PACK, LINKEDIN_ONLY, type BillingPackage } from '../../config/billingPackages';
import { PageShell } from '../../layout/PageShell';
import {
  openBillingPortal,
  startContactPackCheckout,
  startSubscriptionCheckout,
  type BillingInterval,
} from '../../utils/billing';

export type BillingSalesMode = 'page' | 'expired_overlay';

const PLAN_RANK: Record<string, number> = {
  starter: 1,
  growth: 2,
  scale: 3,
  enterprise: 4,
};

const BTN_PRIMARY =
  'inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-btn bg-neutral-90 px-3 text-sm font-semibold text-white transition-colors hover:bg-neutral-90/90';

const BTN_SECONDARY =
  'inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-btn border border-neutral-90/15 bg-white px-3 text-sm font-semibold text-neutral-90 transition-colors hover:bg-neutral-90/[0.04]';

const BTN_INK_INLINE =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-btn bg-neutral-90 px-3 text-sm font-semibold text-white transition-colors hover:bg-neutral-90/90 disabled:cursor-not-allowed disabled:opacity-60';

const PLAN_CARD =
  'flex flex-col rounded-card border border-neutral-90/10 bg-white p-4 text-left';

function ContactPackOffer({ busy, onBuy }: { busy: boolean; onBuy: () => void }) {
  return (
    <div
      className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
      data-testid="billing-contact-pack-row"
    >
      <div className="min-w-0 text-left">
        <p className="text-sm font-semibold text-neutral-90">{CONTACT_PACK.name}</p>
        <p className="mt-0.5 text-sm text-neutral-90/55">
          ${CONTACT_PACK.price} once, for this month. They do not roll over.
        </p>
      </div>
      <button
        type="button"
        data-testid="billing-contact-pack"
        onClick={onBuy}
        disabled={busy}
        className={`${BTN_INK_INLINE} shrink-0 disabled:cursor-not-allowed disabled:opacity-60`}
      >
        {busy ? 'Opening checkout…' : 'Buy 1,000'}
      </button>
    </div>
  );
}

function talkToSalesHref(
  planId: string,
  mailSubject: string,
  calendarUrl: string | null,
): { href: string; external: boolean } {
  if (calendarUrl) {
    try {
      const url = new URL(calendarUrl);
      url.searchParams.set('plan', planId);
      return { href: url.toString(), external: true };
    } catch {
      return { href: calendarUrl, external: true };
    }
  }
  const subject = encodeURIComponent(mailSubject);
  return {
    href: `mailto:${SALES_CONTACT_EMAIL}?subject=${subject}`,
    external: false,
  };
}

function resolveDisplayedPrice(
  pkg: BillingPackage,
  interval: BillingInterval,
): { headline: string; subline: string | null } {
  if (pkg.priceLabel) return { headline: pkg.priceLabel, subline: null };
  if (pkg.monthlyPrice == null) return { headline: 'Custom', subline: null };

  if (interval === 'month') {
    return {
      headline: `$${pkg.monthlyPrice.toLocaleString()}/mo`,
      subline: pkg.yearlyPrice != null ? 'Billed monthly' : null,
    };
  }

  const annualMonthly =
    pkg.annualMonthlyPrice ?? (pkg.yearlyPrice != null ? Math.round(pkg.yearlyPrice / 12) : null);
  if (annualMonthly == null) {
    return {
      headline: `$${pkg.monthlyPrice.toLocaleString()}/mo`,
      subline: 'Billed monthly',
    };
  }

  return {
    headline: `$${annualMonthly.toLocaleString()}/mo`,
    subline: 'Billed monthly',
  };
}

function formatPlanName(planTier: string | null): string {
  if (!planTier) return 'Plan';
  if (planTier === 'extension') return 'LinkedIn only';
  const pkg = BILLING_PACKAGES.find((p) => p.id === planTier);
  return pkg?.name ?? planTier.charAt(0).toUpperCase() + planTier.slice(1);
}

function planCtaLabel(opts: {
  pkg: BillingPackage;
  isCurrent: boolean;
  isActive: boolean;
  isExpired: boolean;
  currentTier: string | null;
}): string {
  const { pkg, isCurrent, isActive, isExpired, currentTier } = opts;
  if (pkg.custom) return 'Talk to sales';
  if (isCurrent) return 'Current plan';
  if (isActive && currentTier) {
    const currentRank = PLAN_RANK[currentTier] ?? 0;
    const targetRank = PLAN_RANK[pkg.id] ?? 0;
    if (targetRank > currentRank) return 'Upgrade';
    if (targetRank < currentRank) return 'Change plan';
    return 'Change plan';
  }
  if (isExpired) return 'Reactivate';
  return 'Get started';
}

interface BillingSalesContentProps {
  mode?: BillingSalesMode;
}

const BillingSalesContent = ({ mode = 'page' }: BillingSalesContentProps) => {
  const { entitlements, loading } = useSubscription();
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('year');
  const [seatCount, setSeatCount] = useState(1);
  const [checkoutKey, setCheckoutKey] = useState<string | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const calendarUrl = entitlements.salesCalendarUrl || SALES_CALENDAR_URL || null;
  const isOverlay = mode === 'expired_overlay';
  const isTrialing = entitlements.access === 'trialing';
  const isActive = entitlements.access === 'active';
  const isExpired = entitlements.access === 'expired' || entitlements.access === 'none';
  const paymentHold = entitlements.billingHold;
  const showPaymentHold = paymentHold === 'grace' || paymentHold === 'paused';
  const showExpiredHeader = !showPaymentHold && (isOverlay || (!loading && isExpired && !isTrialing));
  const currentTier = entitlements.planTier;
  const showPaidAccount = isActive || (showPaymentHold && Boolean(currentTier) && currentTier !== 'trial');

  const title = showExpiredHeader ? 'Your trial has ended' : 'Billing';

  const subtitle = showExpiredHeader
    ? 'Choose a plan to reactivate, or talk to sales for Enterprise.'
    : showPaidAccount
      ? currentTier === 'extension'
        ? 'LinkedIn seats add on. Starter, Growth, and Scale replace the plan you have.'
        : 'LinkedIn seats add on to this plan. A higher plan replaces it.'
        : isTrialing
        ? 'The trial is LinkedIn only. Email starts on a paid plan. Calling is Enterprise.'
        : 'LinkedIn and email on every paid plan. Calling is Enterprise, by conversation.';

  const handleCheckout = async (planId: string, quantity = 1) => {
    const key = `${planId}:${billingInterval}:${quantity}`;
    setActionError(null);
    setCheckoutKey(key);
    try {
      await startSubscriptionCheckout(planId, billingInterval, quantity);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Unable to start checkout.');
      setCheckoutKey(null);
    }
  };

  const handleContactPack = async () => {
    setActionError(null);
    setCheckoutKey('contact-pack');
    try {
      await startContactPackCheckout();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Unable to start checkout.');
      setCheckoutKey(null);
    }
  };

  const handlePortal = async () => {
    setActionError(null);
    setPortalBusy(true);
    try {
      await openBillingPortal();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Unable to open billing portal.');
      setPortalBusy(false);
    }
  };

  const renewLabel = (() => {
    if (!entitlements.currentPeriodEnd) return null;
    const d = new Date(entitlements.currentPeriodEnd);
    if (Number.isNaN(d.getTime())) return null;
    const date = d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return entitlements.cancelAtPeriodEnd ? `Ends ${date}` : `Renews ${date}`;
  })();

  const capacityMeta = [
    entitlements.seats > 0 ? `${entitlements.seats} seats` : null,
    entitlements.maxLinkedInAccounts != null && entitlements.maxLinkedInAccounts > 0
      ? `${entitlements.maxLinkedInAccounts.toLocaleString()} LinkedIn accounts`
      : null,
    entitlements.managedMailboxes > 0
      ? `${entitlements.managedMailboxes} managed mailboxes`
      : null,
    entitlements.estimatedDailySendCapacity > 0
      ? `~${entitlements.estimatedDailySendCapacity.toLocaleString()} emails/day`
      : null,
    entitlements.contactsMonthly
      ? `${entitlements.contactsMonthly.toLocaleString()} contacts / month`
      : null,
    entitlements.billingInterval === 'year'
      ? 'Annual'
      : entitlements.billingInterval === 'month'
        ? 'Monthly'
        : null,
  ].filter(Boolean);

  const trialMeta = [
    entitlements.daysRemaining != null ? `${entitlements.daysRemaining} days left` : null,
    entitlements.trialEndsAt
      ? `Ends ${new Date(entitlements.trialEndsAt).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}`
      : null,
    entitlements.dailyEmailSendCap != null
      ? `${entitlements.dailyEmailSendCap} emails/day per mailbox`
      : null,
  ].filter(Boolean);

  const body = (
    <>
      {showPaymentHold && (
        <section
          className="mb-8 rounded-card border border-neutral-90/10 bg-[#F3E7C8] px-4 py-4 text-left"
          data-testid="billing-payment-hold"
        >
          <p className="text-sm font-medium text-neutral-90">
            {paymentHold === 'paused'
              ? 'Sending is paused until the card on file is updated. Your contacts and campaigns are still here.'
              : `Your card payment failed. Campaigns already sending keep going until ${
                  entitlements.paymentGraceEndsAt
                    ? new Date(entitlements.paymentGraceEndsAt).toLocaleDateString(undefined, {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                      })
                    : 'the grace ends'
                }. Research, enrichment, and new campaigns wait until the card is updated.`}
          </p>
          {entitlements.hasStripeCustomer ? (
            <button
              type="button"
              className={`${BTN_INK_INLINE} mt-3`}
              onClick={() => void handlePortal()}
              disabled={portalBusy}
            >
              {portalBusy ? 'Opening…' : 'Update card'}
            </button>
          ) : null}
        </section>
      )}
      <header className="mb-8 text-left" data-testid={isOverlay ? 'trial-expired-notice' : undefined}>
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-90 sm:text-3xl">{title}</h1>
        <p className="mt-1.5 max-w-2xl text-sm text-neutral-90/55">{subtitle}</p>
      </header>

      {!isOverlay && loading && entitlements.access === 'none' && (
        <div className="mb-8 h-14 animate-pulse rounded-xl bg-neutral-90/5" />
      )}

      {!isOverlay && showPaidAccount && (
        <section
          className="mb-10 border-b border-neutral-90/10 pb-8 text-left"
          data-testid="billing-account-plane"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 space-y-1.5">
              <p className="text-base font-semibold text-neutral-90">
                {formatPlanName(currentTier)}
                {currentTier ? (
                  <span className="ml-2 text-xs font-medium uppercase tracking-wide text-neutral-90/40">
                    Current
                  </span>
                ) : null}
              </p>
              <p className="text-sm text-neutral-90/50">
                {[renewLabel, ...capacityMeta].filter(Boolean).join(' · ') || 'Active subscription'}
              </p>
            </div>
            {entitlements.hasStripeCustomer ? (
              <button
                type="button"
                data-testid="billing-manage-portal"
                onClick={() => void handlePortal()}
                disabled={portalBusy}
                className={BTN_INK_INLINE}
              >
                {portalBusy ? 'Opening…' : 'Manage billing'}
              </button>
            ) : (
              <p className="text-sm text-neutral-90/45">
                Billing portal unavailable — talk to sales to manage this account.
              </p>
            )}
          </div>
          <div className="mt-4 border-t border-neutral-90/10 pt-4">
            <ContactPackOffer busy={checkoutKey === 'contact-pack'} onBuy={() => void handleContactPack()} />
          </div>
        </section>
      )}

      {!isOverlay && isTrialing && (
        <p className="mb-8 text-sm text-neutral-90/50" data-testid="billing-trial-meta">
          {trialMeta.join(' · ')}
        </p>
      )}

      {!isOverlay && !showPaidAccount && (
        <div className="mb-8 border-b border-neutral-90/10 pb-6">
          <ContactPackOffer busy={checkoutKey === 'contact-pack'} onBuy={() => void handleContactPack()} />
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm font-medium text-neutral-90">
          {showPaidAccount ? 'Change plan' : 'Plans'}
        </p>
        <div className="flex items-center gap-3" role="tablist" aria-label="Billing interval">
          <button
            type="button"
            role="tab"
            aria-selected={billingInterval === 'month'}
            onClick={() => setBillingInterval('month')}
            className={`border-b-2 pb-1 text-sm transition-colors ${
              billingInterval === 'month'
                ? 'border-neutral-90 font-medium text-neutral-90'
                : 'border-transparent text-neutral-90/45 hover:text-neutral-90/70'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={billingInterval === 'year'}
            onClick={() => setBillingInterval('year')}
            className={`border-b-2 pb-1 text-sm transition-colors ${
              billingInterval === 'year'
                ? 'border-neutral-90 font-medium text-neutral-90'
                : 'border-transparent text-neutral-90/45 hover:text-neutral-90/70'
            }`}
          >
            Annual
          </button>
          {billingInterval === 'year' && (
            <span className="pb-1 text-xs text-neutral-90/40">Save ~15%</span>
          )}
        </div>
      </div>

      {actionError && (
        <div
          className="mb-4 rounded-card border border-red-500/20 bg-red-50 px-4 py-3 text-sm text-red-700"
          role="alert"
        >
          {actionError}
        </div>
      )}

      {(() => {
        const pkg = LINKEDIN_ONLY;
        const each = billingInterval === 'year' ? pkg.annualMonthlyPrice ?? pkg.monthlyPrice : pkg.monthlyPrice;
        const seatTotal = (each ?? 0) * seatCount;
        const contacts = seatCount * 400;
        const checkoutId = `${pkg.id}:${billingInterval}:${seatCount}`;
        const onPaidPlan = showPaidAccount && !!currentTier && currentTier !== 'extension';
        const addingSeat = onPaidPlan || (showPaidAccount && currentTier === 'extension');
        const features = onPaidPlan
          ? [
              `+${seatCount} LinkedIn ${seatCount === 1 ? 'account' : 'accounts'}`,
              `+${contacts.toLocaleString()} enriched contacts with signals / month`,
              'The plan you have stays',
            ]
          : [
              `${seatCount} LinkedIn ${seatCount === 1 ? 'account' : 'accounts'}`,
              `${contacts.toLocaleString()} enriched contacts with signals / month`,
              'No email and no calling',
            ];
        const busy = checkoutKey === checkoutId;
        const seatLabel = seatCount === 1 ? 'Add a LinkedIn seat' : `Add ${seatCount} LinkedIn seats`;
        return (
          <article
            className={`${PLAN_CARD} mb-4`}
            data-testid="billing-plan-extension"
          >
            <div className="min-w-0 sm:max-w-xl">
              <h2 className="text-base font-semibold text-neutral-90">
                {onPaidPlan ? 'LinkedIn seats' : pkg.name}
              </h2>
              <p className="mt-1 text-sm text-neutral-90/50">
                {onPaidPlan
                  ? 'Add LinkedIn accounts to the plan you already have.'
                  : pkg.tagline}
              </p>
              <p className="mt-3 text-xl font-semibold tracking-tight text-neutral-90">
                ${seatTotal.toLocaleString()}/mo
              </p>
              <p className="mt-0.5 text-xs text-neutral-90/40">
                ${each?.toLocaleString()} each · billed monthly{onPaidPlan ? ', added to your plan' : ''}
              </p>
              <div className="mt-3 flex items-center gap-2" role="group" aria-label="LinkedIn seats">
                <button
                  type="button"
                  aria-label="Fewer seats"
                  onClick={() => setSeatCount((count) => Math.max(1, count - 1))}
                  className="min-h-9 min-w-9 rounded-btn border border-neutral-90/15 text-sm text-neutral-90"
                >
                  −
                </button>
                <span className="min-w-6 text-center text-sm font-medium text-neutral-90" data-testid="billing-seat-count">
                  {seatCount}
                </span>
                <button
                  type="button"
                  aria-label="More seats"
                  onClick={() => setSeatCount((count) => Math.min(25, count + 1))}
                  className="min-h-9 min-w-9 rounded-btn border border-neutral-90/15 text-sm text-neutral-90"
                >
                  +
                </button>
              </div>
              <ul className="mt-4 space-y-2">
                {features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-neutral-90/70">
                    <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-neutral-90/30" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <button
              type="button"
              data-testid="billing-checkout-extension"
              onClick={() => void handleCheckout(pkg.id, seatCount)}
              disabled={checkoutKey !== null}
              aria-busy={busy}
              className={`${BTN_PRIMARY} mt-5 disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {busy ? 'Opening checkout…' : addingSeat ? seatLabel : 'Get started'}
            </button>
          </article>
        );
      })()}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {BILLING_PACKAGES.map((pkg) => {
          const cta = talkToSalesHref(pkg.id, pkg.mailSubject, calendarUrl);
          const { headline, subline } = resolveDisplayedPrice(pkg, billingInterval);
          const checkoutId = `${pkg.id}:${billingInterval}`;
          const isCurrent = showPaidAccount && currentTier === pkg.id;
          const label = planCtaLabel({
            pkg,
            isCurrent,
            isActive: showPaidAccount,
            isExpired: showExpiredHeader,
            currentTier,
          });
          const busy = checkoutKey === checkoutId;

          return (
            <article
              key={pkg.id}
              className={`${PLAN_CARD} ${isCurrent ? 'border-neutral-90/25' : ''}`}
              data-testid={`billing-plan-${pkg.id}`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="text-base font-semibold text-neutral-90">{pkg.name}</h2>
                {pkg.popular && !isCurrent && (
                  <span className="text-[11px] font-medium text-neutral-90/40">Recommended</span>
                )}
                {isCurrent && (
                  <span className="text-[11px] font-medium uppercase tracking-wide text-neutral-90/40">
                    Current
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-neutral-90/50">{pkg.tagline}</p>
              <p className="mt-3 text-xl font-semibold tracking-tight text-neutral-90">{headline}</p>
              {subline && <p className="mt-0.5 text-xs text-neutral-90/40">{subline}</p>}

              <ul className="mt-4 flex-1 space-y-2 border-t border-neutral-90/10 pt-3">
                {pkg.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-neutral-90/70">
                    <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-neutral-90/30" />
                    {feature}
                  </li>
                ))}
              </ul>

              {pkg.custom ? (
                <a
                  href={cta.href}
                  target={cta.external ? '_blank' : undefined}
                  rel={cta.external ? 'noopener noreferrer' : undefined}
                  className={`${BTN_SECONDARY} mt-5`}
                >
                  Talk to sales
                </a>
              ) : isCurrent ? (
                entitlements.hasStripeCustomer ? (
                  <button
                    type="button"
                    data-testid={`billing-current-${pkg.id}`}
                    onClick={() => void handlePortal()}
                    disabled={portalBusy}
                    className={`${BTN_SECONDARY} mt-5 disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    {portalBusy ? 'Opening…' : 'Manage billing'}
                  </button>
                ) : (
                  <button type="button" disabled className={`${BTN_SECONDARY} mt-5 opacity-60`}>
                    Current plan
                  </button>
                )
              ) : (
                <button
                  type="button"
                  data-testid={`billing-checkout-${pkg.id}`}
                  onClick={() => void handleCheckout(pkg.id)}
                  disabled={checkoutKey !== null}
                  aria-busy={busy}
                  className={`${BTN_PRIMARY} mt-5 disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  {busy ? 'Opening checkout…' : label}
                </button>
              )}
            </article>
          );
        })}
      </div>

    </>
  );

  if (isOverlay) {
    return (
      <div className="min-h-screen bg-bone">
        <div className="mx-auto w-full max-w-none px-4 py-8 sm:px-6 lg:px-8">{body}</div>
      </div>
    );
  }

  return (
    <PageShell width="full" bg="white" innerClassName="!px-4 !py-5 sm:!px-6 lg:!px-8">
      {body}
    </PageShell>
  );
};

export default BillingSalesContent;
